import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Wind,
  Square,
  Droplets,
  Waves,
  Hand,
  CalendarCheck,
  Check,
  ArrowRight,
} from "lucide-react";

const INCLUSIONS = [
  {
    icon: Sparkles,
    title: "Deep Restroom Sanitization",
    desc: "Hospital-grade disinfection of every fixture, surface, and corner.",
  },
  {
    icon: Wind,
    title: "Odor Elimination & Control",
    desc: "Eliminate the source — not just mask it. Long-lasting freshness.",
  },
  {
    icon: Square,
    title: "Mirror & Glass Polishing",
    desc: "Streak-free, spot-free mirrors and glass partitions — crystal clear every visit.",
  },
  {
    icon: Droplets,
    title: "Urinal & Toilet Descaling",
    desc: "Break down hardened mineral and uric scale build-up.",
  },
  {
    icon: Waves,
    title: "Floor Pressure Washing",
    desc: "Optional foam treatment for the deepest restoration of restroom floors.",
  },
  {
    icon: Hand,
    title: "High-Touch Disinfection",
    desc: "Door handles, faucets, dispensers, switches — every touchpoint.",
  },
  {
    icon: CalendarCheck,
    title: "Maintenance Programs",
    desc: "Daily, weekly, or monthly programs tailored to your facility.",
  },
];

const Services = ({ onBook }) => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative border-y border-slate-200 bg-slate-50 py-20 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-16 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-7">
            <p className="mb-6 font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Our Service
            </p>
            <h2
              data-testid="services-heading"
              className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
            >
              One complete service.
              <br />
              <span className="text-blue-600">Seven layers of clean.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Every Deep Clean visit is a single, comprehensive restroom
              treatment — no piecemeal pricing, no add-ons. Here's exactly
              what's included.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Left summary card — establishes "one service" framing */}
          <div className="lg:col-span-4">
            <div
              data-testid="service-summary-card"
              className="sticky top-24 overflow-hidden rounded-md border border-slate-200 bg-slate-950 p-8 text-white md:p-10"
            >
              <div
                className="absolute inset-0 opacity-50"
                aria-hidden="true"
                style={{
                  background:
                    "radial-gradient(60% 50% at 80% 0%, rgba(6,182,212,0.28), transparent 70%), radial-gradient(60% 60% at 0% 100%, rgba(0,85,255,0.45), transparent 70%)",
                }}
              />
              <div className="relative">
                <p className="font-body text-[10px] font-semibold uppercase tracking-[0.28em] text-cyan-300">
                  The Deep Clean Service
                </p>
                <h3 className="mt-4 font-heading text-3xl font-black leading-tight tracking-tighter text-white">
                  One visit.
                  <br />
                  Everything covered.
                </h3>
                <p className="mt-5 font-body text-sm leading-relaxed text-slate-300">
                  All seven inclusions are part of every Deep Clean treatment.
                  You don't pick and choose — we don't cut corners.
                </p>
                <button
                  data-testid="services-cta-btn"
                  onClick={onBook}
                  className="group mt-8 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-blue-600 font-body text-sm font-bold tracking-wide text-white transition-colors hover:bg-blue-500"
                >
                  Request a Quote
                  <ArrowRight
                    size={16}
                    strokeWidth={2.2}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </button>
                <Link
                  to="/services"
                  data-testid="services-details-link"
                  className="group mt-3 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md border border-white/20 bg-white/5 font-body text-sm font-bold tracking-wide text-white transition-colors hover:bg-white/10"
                >
                  Full service details
                  <ArrowRight
                    size={16}
                    strokeWidth={2.2}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </div>
          </div>

          {/* Right inclusions list — clearly framed as "what's included" */}
          <div className="lg:col-span-8">
            <ol
              data-testid="service-inclusions-list"
              className="overflow-hidden rounded-md border border-slate-200 bg-white"
            >
              {INCLUSIONS.map((s, i) => (
                <li
                  key={s.title}
                  data-testid={`service-inclusion-${i}`}
                  className="group grid grid-cols-[auto,auto,1fr,auto] items-start gap-5 border-b border-slate-200 p-6 transition-colors last:border-b-0 hover:bg-slate-50 md:p-8"
                >
                  <span className="mt-1 font-heading text-sm font-bold tabular-nums text-slate-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="grid h-11 w-11 place-items-center rounded-md border border-slate-200 bg-white text-blue-600 transition-colors group-hover:border-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                    <s.icon size={20} strokeWidth={1.6} />
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 md:text-xl">
                      {s.title}
                    </h3>
                    <p className="mt-1.5 font-body text-sm font-medium leading-relaxed text-slate-600">
                      {s.desc}
                    </p>
                  </div>
                  <span
                    className="mt-1 inline-flex h-6 items-center gap-1 rounded-full bg-blue-50 px-2.5 font-body text-[10px] font-bold uppercase tracking-wider text-blue-700"
                    data-testid={`service-included-badge-${i}`}
                  >
                    <Check size={12} strokeWidth={3} />
                    Included
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
